import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Search, 
  Refresh,
  Payment,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Receipt,
  Person,
  AttachMoney,
  Business,
  Description,
  Edit,
  Save,
  Cancel
} from '@mui/icons-material';

const UpdatePayoutPayment = () => {
  // Search form state
  const [searchForm, setSearchForm] = useState({
    fromDate: '',
    toDate: '',
    payoutNo: '',
    memberId: '',
  });
  
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sample payout payment data
  const [payoutPayments] = useState([
    {
      id: 1,
      payoutNo: 'PO001',
      memberId: 'M001',
      memberName: 'John Smith',
      paymentAmount: 15000,
      paymentDate: '2024-01-15',
      status: 'Pending',
      paymentMethod: 'Bank Transfer',
    },
    {
      id: 2,
      payoutNo: 'PO002',
      memberId: 'M002',
      memberName: 'Sarah Johnson',
      paymentAmount: 22000,
      paymentDate: '2024-01-20',
      status: 'Completed',
      paymentMethod: 'UPI',
    },
    {
      id: 3,
      payoutNo: 'PO003',
      memberId: 'M003',
      memberName: 'Michael Brown',
      paymentAmount: 18500,
      paymentDate: '2024-01-25',
      status: 'Pending',
      paymentMethod: 'Cheque',
    },
  ]);

  const handleSearchFormChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate date range if both dates are provided
    if (searchForm.fromDate && searchForm.toDate) {
      const fromDate = new Date(searchForm.fromDate);
      const toDate = new Date(searchForm.toDate);
      
      if (fromDate > toDate) {
        newErrors.toDate = 'To date cannot be earlier than From date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = () => {
    if (!validateForm()) {
      return;
    }

    console.log('Searching with data:', searchForm);
    setSuccessMessage(`Search completed! Found ${payoutPayments.length} payout payment record(s).`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleReset = () => {
    setSearchForm({
      fromDate: '',
      toDate: '',
      payoutNo: '',
      memberId: '',
    });
    setErrors({});
    setSuccessMessage('');
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getFilteredPayoutData = () => {
    let filteredData = payoutPayments;

    // Filter by payout number
    if (searchForm.payoutNo.trim()) {
      filteredData = filteredData.filter(item =>
        item.payoutNo.toLowerCase().includes(searchForm.payoutNo.toLowerCase())
      );
    }

    // Filter by member ID
    if (searchForm.memberId.trim()) {
      filteredData = filteredData.filter(item =>
        item.memberId.toLowerCase().includes(searchForm.memberId.toLowerCase())
      );
    }

    // Filter by date range
    if (searchForm.fromDate && searchForm.toDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = new Date(item.paymentDate);
        const fromDate = new Date(searchForm.fromDate);
        const toDate = new Date(searchForm.toDate);
        return itemDate >= fromDate && itemDate <= toDate;
      });
    }

    return filteredData;
  };

  const getSearchSummary = () => {
    const total = getFilteredPayoutData().length;
    const totalAmount = getFilteredPayoutData().reduce((sum, payment) => sum + payment.paymentAmount, 0);
    return `Showing ${total} payout payment record(s) with total amount of ${formatCurrency(totalAmount)}`;
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Failed':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  // Summary calculations
  const totalPayments = payoutPayments.length;
  const pendingCount = payoutPayments.filter(p => p.status === 'Pending').length;
  const completedCount = payoutPayments.filter(p => p.status === 'Completed').length;
  const totalAmount = payoutPayments.reduce((sum, payment) => sum + payment.paymentAmount, 0);

  const filteredData = getFilteredPayoutData();
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Payment sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
        Update Payout Payment
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircle />}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Payments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalPayments}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Pending Payments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {pendingCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Completed Payments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {completedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#f3e5f5', 
            border: '1px solid #ce93d8',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <AttachMoney sx={{ color: '#7b1fa2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalAmount / 1000).toFixed(0)}K
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          {/* Search Section */}
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              Search Payout Payments
            </Typography>
          </Box>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={searchForm.fromDate}
              onChange={(e) => handleSearchFormChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.fromDate}
                helperText={errors.fromDate}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={searchForm.toDate}
              onChange={(e) => handleSearchFormChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.toDate}
                helperText={errors.toDate}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Payout No"
              value={searchForm.payoutNo}
              onChange={(e) => handleSearchFormChange('payoutNo', e.target.value)}
              placeholder="Enter payout number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Member ID"
              value={searchForm.memberId}
              onChange={(e) => handleSearchFormChange('memberId', e.target.value)}
              placeholder="Enter member ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          <Box display="flex" gap={2} mb={3}>
              <Button
                variant="contained"
              startIcon={<Search />}
                onClick={handleSearch}
              sx={{ 
                minWidth: 120,
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Search Payments
              </Button>
              
              <Button
                variant="outlined"
              startIcon={<Refresh />}
                onClick={handleReset}
              sx={{ 
                minWidth: 100,
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
              >
                Reset
              </Button>
            </Box>

          {/* Search Summary */}
          {/* {filteredData.length > 0 && (
            <Alert 
              severity="info" 
              icon={<Info />}
              sx={{ mb: 3 }}
            >
              {getSearchSummary()}
            </Alert>
          )} */}

          {/* Results Table */}
          {filteredData.length > 0 ? (
            <>
              <TableContainer sx={{ boxShadow: 1, borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        Payout Info
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        Member Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        Payment Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((payment) => (
                      <TableRow 
                        key={payment.id}
                        hover
                        sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ fontSize: 16, color: '#1976d2' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {payment.payoutNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Payment Date: {payment.paymentDate}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {payment.memberName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {payment.memberId}
        </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#f57c00' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                                {formatCurrency(payment.paymentAmount)}
          </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {payment.paymentMethod}
          </Typography>
        </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            label={payment.status}
                            sx={getStatusStyle(payment.status)}
                            size="small"
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Update Payment">
                              <IconButton 
                                size="small" 
                                color="primary"
                                sx={{ 
                                  backgroundColor: '#e3f2fd',
                                  '&:hover': { backgroundColor: '#bbdefb' }
                                }}
                              >
                                <Edit sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                color="info"
                                sx={{ 
                                  backgroundColor: '#e1f5fe',
                                  '&:hover': { backgroundColor: '#b3e5fc' }
                                }}
                              >
                                <Info sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ 
                  borderTop: '1px solid #e0e0e0',
                  mt: 2
                }}
              />
            </>
          ) : (
            <Alert 
              severity="warning" 
              icon={<Warning />}
              sx={{ mt: 3 }}
            >
              No payout payment records found. Please adjust your search criteria and try again.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UpdatePayoutPayment;